import { useParams, Navigate, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Check, ArrowLeft } from "lucide-react";
import { Section, Container, Eyebrow, Reveal } from "../components/site/primitives";
import { CTABanner } from "../components/site/blocks";
import { SEGMENTS, SEGMENT_LIST } from "../lib/content";
import Seo from "../components/site/Seo";

const SEO_MAP = {
  "fintech-lenders": {
    title: "Fintech & Lender Partnerships | Community Tax",
    description: "Remove tax debt as a barrier to funded loans. Embedded resolution for fintech lending platforms, banks, and credit unions.",
  },
  "tax-software": {
    title: "Tax Software Provider Partnerships | Community Tax",
    description: "Turn balance-due filers into a retention and revenue opportunity with embedded representation.",
  },
  "law-firms": {
    title: "Law Firm Partnerships | Community Tax",
    description: "Retain more clients without expanding your tax practice — Community Tax services cases below your threshold.",
  },
  "financial-advisors": {
    title: "Financial Advisor Partnerships | Community Tax",
    description: "Protect the wealth plans you've built from IRS action with a trusted referral partner.",
  },
};

export default function Segment() {
  const { slug } = useParams();
  const seg = SEGMENTS[slug];
  if (!seg) return <Navigate to="/who-we-serve" replace />;
  const Icon = (seg.icon && Icons[seg.icon]) || Icons.Layers;

  return (
    <>
      <Seo
        title={SEO_MAP[seg.slug]?.title || `${seg.nav} | Community Tax`}
        description={SEO_MAP[seg.slug]?.description || seg.hook}
        path={`/who-we-serve/${seg.slug}`}
      />
      <section className="pt-14 pb-10 lg:pt-20" data-testid={`segment-page-${seg.slug}`}>
        <Container>
          <Link
            to="/who-we-serve"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#40424D] hover:text-[#0262B4] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Who We Serve
          </Link>
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8">
              <Eyebrow>{seg.nav}</Eyebrow>
              <Reveal>
                <h1 className="font-display font-extrabold text-[#1E1E24] text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.06] tracking-tight">
                  {seg.headline}
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-lg text-[#40424D] leading-relaxed max-w-2xl">{seg.body}</p>
              </Reveal>
            </div>
            <div className="lg:col-span-4">
              <div className="ct-grad-navy rounded-[28px] p-10 flex items-center justify-center aspect-square">
                <Icon className="w-20 h-20 text-white" strokeWidth={1.25} />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Benefits */}
      <Section className="bg-white">
        <Eyebrow>Benefits</Eyebrow>
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-7 mt-8">
          {seg.benefits.map((b, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="flex items-start gap-4">
                <span className="mt-0.5 w-8 h-8 rounded-full bg-[#0262B4]/12 flex items-center justify-center shrink-0">
                  <Check className="w-4 h-4 text-[#0262B4]" strokeWidth={3} />
                </span>
                <p className="text-lg font-medium text-[#1E1E24] leading-relaxed">{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Ideal For */}
      <Section>
        <Eyebrow>Ideal For</Eyebrow>
        <div className="flex flex-wrap gap-3 mt-6">
          {seg.idealFor.map((t, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <span className="inline-block bg-white border border-slate-200 rounded-full px-5 py-2.5 text-sm font-medium text-[#1E1E24] ct-shadow-card">
                {t}
              </span>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Without a partner path */}
      <Section className="bg-white">
        <Eyebrow>Without a Partner Path</Eyebrow>
        <Reveal>
          <h2 className="font-display font-extrabold text-[#1E1E24] text-3xl md:text-4xl leading-tight max-w-3xl mb-12">
            What happens when there's no path forward
          </h2>
        </Reveal>
        <div className="bg-slate-50 border border-slate-200 rounded-[24px] p-8 lg:p-12 max-w-4xl">
          <ul className="space-y-5">
            {seg.without.map((t, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                    <Icons.X className="w-3.5 h-3.5 text-red-500" strokeWidth={3} />
                  </span>
                  <span className="text-[#40424D] leading-snug">{t}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 text-[#1E1E24] font-medium text-lg leading-relaxed max-w-3xl">
            {seg.closing}
          </p>
        </Reveal>
      </Section>

      <CTABanner />
    </>
  );
}
